type IdentifiableMarkup = {
  currency?: string | null;
  markupValue?: number | null;
};

const getCurrencyLabel = (currency: string | null | undefined) => {
  if (!currency || currency.trim() === '') {
    return 'ANY';
  }

  return currency.trim();
};

const getValueLabel = (markupValue: number | null | undefined) => {
  return markupValue === null || markupValue === undefined ? null : String(markupValue);
};

const toSuffix = (index: number) => String.fromCharCode(65 + index);

/**
 * Generates stable human-readable identifiers for saved channel markup rows.
 *
 * Rules:
 * - If a currency appears only once, use the currency alone.
 * - If a currency has multiple values, include the markup value.
 * - If both currency and value collide, append -A, -B, ...
 */
export function buildChannelMarkupIdentifiers(markups: Array<IdentifiableMarkup>): Array<string> {
  const currencyCounts = new Map<string, number>();
  const groupCounts = new Map<string, number>();
  const groupSeen = new Map<string, number>();

  for (const markup of markups) {
    const currency = getCurrencyLabel(markup.currency);
    const value = getValueLabel(markup.markupValue);
    const groupKey = value ? `${currency}|${value}` : `${currency}|__NO_VALUE__`;

    currencyCounts.set(currency, (currencyCounts.get(currency) ?? 0) + 1);
    groupCounts.set(groupKey, (groupCounts.get(groupKey) ?? 0) + 1);
  }

  return markups.map((markup) => {
    const currency = getCurrencyLabel(markup.currency);
    const value = getValueLabel(markup.markupValue);
    const currencyCount = currencyCounts.get(currency) ?? 0;
    const groupKey = value ? `${currency}|${value}` : `${currency}|__NO_VALUE__`;
    const groupCount = groupCounts.get(groupKey) ?? 0;
    const groupIndex = groupSeen.get(groupKey) ?? 0;

    groupSeen.set(groupKey, groupIndex + 1);

    if (currencyCount === 1) {
      return currency;
    }

    const base = value ? `${currency}-${value}` : currency;
    if (groupCount === 1) {
      return base;
    }

    return `${base}-${toSuffix(groupIndex)}`;
  });
}
